/**
 * The one-Continue email flow, shared by BOTH clients: try sign-in, fall back
 * to sign-up + email-OTP verification. Mobile has always worked this way
 * ((auth)/_layout.tsx); the web AuthCard adopted it in the same change that
 * lifted the logic here, so the two can never drift again.
 *
 * Per this package's rule (see ./index.ts) this file imports nothing from
 * better-auth: the client arrives as a minimal structural slice both apps'
 * clients satisfy.
 *
 * THE INVARIANT CALLERS MUST HONOR — sign-up never reveals whether an account
 * exists. The worker's `requireEmailVerification` makes better-auth 1.7 answer
 * an existing email with a synthetic 200 (fake user, no session) so attackers
 * cannot probe addresses, and the worker's `onExistingUserSignUp` hook emails
 * that user the same verification OTP a fresh sign-up gets. `needs-otp`
 * therefore means "a code is on its way to that inbox" and NOTHING more: UI
 * copy must say "we sent a code", never "account created" (the ghost-signup
 * bug of 2026-08-31, where an existing google-only user was told an account
 * was created and no code existed).
 */
import { type AuthErrorLike, mapAuthError } from './auth-error';
import { isRateLimitedError } from './rate-limit';

export type EmailContinueOutcome = 'signed-in' | 'needs-otp' | 'needs-verification';

type AuthResult = { error?: AuthErrorLike };

/** The slice of a Better Auth client this flow drives (structural on purpose). */
export interface EmailContinueClient {
  signIn: {
    email(input: { email: string; password: string }): Promise<AuthResult>;
  };
  signUp: {
    email(input: { email: string; password: string; name: string }): Promise<AuthResult>;
  };
  emailOtp: {
    sendVerificationOtp(input: {
      email: string;
      type: 'email-verification';
    }): Promise<AuthResult>;
  };
}

export type EmailContinueResult =
  | { outcome: EmailContinueOutcome; error?: undefined }
  /**
   * `error` is already NORMALIZED for display: feed it to `mapAuthError` (or
   * read `errorKey`) and the user sees the right copy. In particular a
   * duplicate-email sign-up error is rewritten to invalid-credentials —
   * reporting "email already registered" after a failed sign-in would be the
   * account-existence oracle the whole flow is built to avoid.
   */
  | { outcome?: undefined; error: NonNullable<AuthErrorLike> };

export async function emailContinue(
  client: EmailContinueClient,
  email: string,
  password: string,
): Promise<EmailContinueResult> {
  const signIn = await client.signIn.email({ email, password });
  if (!signIn.error) return { outcome: 'signed-in' };

  // Refused by the rate limiter, the sign-in was never ATTEMPTED — it says
  // nothing about the password. Falling through to sign-up here took a right
  // password to "we sent a code" (the anti-enumeration sign-up answers an
  // existing email with a synthetic success and emails a code), spending two
  // more limited calls and an email on a user who only had to wait. Returned
  // as is, so `waitOutRateLimit` runs the whole flow again.
  if (isRateLimitedError(signIn.error)) return { error: signIn.error };

  // An abandoned sign-up leaves a REAL account behind whose email was never
  // verified; `requireEmailVerification` makes sign-in answer 403
  // EMAIL_NOT_VERIFIED for it. Without this branch the sign-up below reports
  // "already exists" → "invalid email or password", and the account is locked
  // out for good with no route back to the code step. Send a fresh
  // verification code and hand the caller the step that owns the resend
  // button instead.
  if (signIn.error.code === 'EMAIL_NOT_VERIFIED') {
    const resend = await client.emailOtp.sendVerificationOtp({
      email,
      type: 'email-verification',
    });
    if (resend.error) return { error: resend.error };
    return { outcome: 'needs-verification' };
  }

  // Name is deliberately empty: it is collected later, when the user enables
  // the social features — not at the door.
  const signUp = await client.signUp.email({ email, password, name: '' });
  if (!signUp.error) return { outcome: 'needs-otp' };

  // Existing account + wrong password lands here IF the worker ever surfaces
  // a duplicate-email error again (1.6 did; 1.7's anti-enumeration sign-up
  // answers a synthetic 200 instead, so normally this branch is dead — kept
  // because a requireEmailVerification/autoSignIn config tweak revives it).
  // Report bad credentials, not "email exists".
  if (
    signUp.error.code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL' ||
    signUp.error.code === 'USER_ALREADY_EXISTS'
  ) {
    return { error: { ...signUp.error, code: 'INVALID_EMAIL_OR_PASSWORD' } };
  }
  return { error: signUp.error };
}

/** Convenience for callers that want the i18n key rather than the raw error. */
export function emailContinueErrorKey(result: EmailContinueResult) {
  return result.error ? mapAuthError(result.error) : null;
}
